import { message } from "antd";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { objectDetailSelector } from "redux/modules/common/building/object/nowObject";

import icon from "pages/HandBook/assets/handbook.svg";
import { getHandbookTemplates, getHandbookTemplatesMore, sendTemplate } from "pages/HandBook/model/thunks";
import { IHandBookTemplate } from "pages/HandBook/model/types";

import { useBuildingInvalidateKeyContext } from "./BuildingInvalidateKeyContext";
import { useTypedSelector } from "app/store/typedUseSelector";
import { useConfirmModal } from "entities/ConfirmationModal/ConfirmationContext";
import Spinner from "shared/ui/atoms/Spinner/Spinner";
import TableReusableRow, { TableReusableCell } from "shared/ui/dataDisplay/TableReusable/TableReusableRow";
import ListWrapper from "shared/ui/layout/ListWrapper/ListWrapper";
import RowGrid from "shared/ui/layout/RowGrid/RowGrid";

import { useTypedParams } from "utils/hooks/useTypedParams";

import { generateStorageKey } from "utils/helpers/generateStorageKey";

import styles from "./BuildingImportTemplate.module.scss";

interface IProps {
  onClose?: () => void;
}

const BuildingImportTemplate = ({ onClose }: IProps) => {
  const { objectId } = useTypedParams();
  const dispatch = useDispatch();
  const key = generateStorageKey({} as any);
  const moreKey = generateStorageKey({ type: "moreTemplates" } as any);
  const templates = useTypedSelector((state) => state.handBookReducer.templates)[key];
  const isLoading = useTypedSelector((state) => state.handBookReducer.loadings)[key];
  const isLoadingMore = useTypedSelector((state) => state.handBookReducer.loadings)[moreKey];
  const objectInfo = useSelector(objectDetailSelector);
  const pendings = useTypedSelector((state) => state.handBookReducer.loadings);

  useEffect(() => {
    dispatch(getHandbookTemplates({ limit: Math.max(templates?.results?.length ?? 10, 10) + 1 }));
  }, []);

  const { openConfirmModal, closeConfirmModal } = useConfirmModal();

  const { revalidate } = useBuildingInvalidateKeyContext();

  const openDialog = (template: IHandBookTemplate) => () => {
    openConfirmModal({
      isOpen: true,
      onClose: closeConfirmModal,
      action: () => {
        closeConfirmModal();
        dispatch(
          sendTemplate({
            objectId: objectId as string,
            templateId: template.id,
            successCallback: () => {
              message.success(`Шаблон из справочника успешно импортирован на объект`);
              onClose?.();
              revalidate();
            },
          })
        );
      },
      title: "Подтвердите действие",
      acceptButtonText: "Загрузить",
      className: styles.confirmModal,
      children: `Вы действительно хотите выгрузить шаблон ${template.name} на объект ${objectInfo?.name || objectId}`,
    });
  };

  return (
    <div className={styles.list}>
      <ListWrapper
        data={templates?.results ?? []}
        dataMapper={(item: IHandBookTemplate) => {
          const isPending = pendings[generateStorageKey({ type: "send", id: item.id, objectId })];
          return (
            <TableReusableRow
              onClick={openDialog(item)}
              key={item.id}
              containerClassName={styles.container}
              className={styles.item}
            >
              <RowGrid percents={[100]}>
                <TableReusableCell className={styles.cell} isNoBreak>
                  {isPending ? (
                    <div className={styles.spinner}>
                      <Spinner isSmallGray isStatic />
                    </div>
                  ) : (
                    item.name
                  )}
                </TableReusableCell>
              </RowGrid>
            </TableReusableRow>
          );
        }}
        placeholderProps={{ img: icon, text: "Нет сведений" }}
        total={templates?.count}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onPagination={() => {
          dispatch(getHandbookTemplatesMore({ offset: templates?.results?.length }));
        }}
      />
    </div>
  );
};

export default BuildingImportTemplate;
